const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8050';

const request = async (path, options = {}) => {
  const response = await fetch(`${API_BASE_URL}/api/v1/cohort${path}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (response.status === 204) return null;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.detail || 'Cohort request failed');
  return payload;
};

export const fetchAllCohortKeys = () => request('/all-keys');

export const fetchCohorts = () => request('');

export const fetchMyCohort = () => request('/my');

export const createCohort = (data) =>
  request('', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const addCohortMembership = (cohortKey, data) =>
  request(`/${encodeURIComponent(cohortKey)}/memberships`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const fetchCohortMembers = (cohortKey) =>
  request(`/${encodeURIComponent(cohortKey)}/members`);

export const saveCohortScheduleCoordinator = (cohortKey, userId) =>
  request(`/${encodeURIComponent(cohortKey)}/schedule-coordinator`, {
    method: 'PUT',
    body: JSON.stringify({ user_id: userId }),
  });

export const fetchCohortWeeks = (cohortKey) => request(`/${encodeURIComponent(cohortKey)}/weeks`);

export const fetchCohortWeek = (cohortKey, weekNumber) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}`);

export const saveCohortWeek = (cohortKey, weekNumber, data) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const saveCohortAvailability = (cohortKey, weekNumber, slots) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/availability`, {
    method: 'PUT',
    body: JSON.stringify({ slots }),
  });

export const selectCohortAvailabilitySlot = (cohortKey, weekNumber, slot) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/availability/select`, {
    method: 'POST',
    body: JSON.stringify({ slot }),
  });

export const saveCohortScheduleAvailability = (cohortKey, weekNumber, activity, slots) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/schedule/${encodeURIComponent(activity)}/availability`, {
    method: 'PUT',
    body: JSON.stringify({ slots }),
  });

export const selectCohortScheduleSlot = (cohortKey, weekNumber, activity, slot) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/schedule/${encodeURIComponent(activity)}/select`, {
    method: 'POST',
    body: JSON.stringify({ slot }),
  });

export const notifyCohortWeekRoles = (cohortKey, weekNumber) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/notify-roles`, {
    method: 'POST',
  });

export const generateCohortWeek = (cohortKey, weekNumber, trackSlug, curriculumWeek, teamSize = 5) => {
  const params = new URLSearchParams({
    track_slug: trackSlug,
    curriculum_week: curriculumWeek,
    team_size: teamSize,
  });
  return request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/generate?${params}`, {
    method: 'POST',
  });
};

export const updateCohortRole = (cohortKey, weekNumber, roleId, patch) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/role/${encodeURIComponent(roleId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const fetchHackathonAttempts = (cohortKey, weekNumber) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/attempts`);

export const startHackathonAttempt = (cohortKey, weekNumber, roleId) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/role/${encodeURIComponent(roleId)}/attempt/start`, {
    method: 'POST',
  });

export const submitHackathonAttempt = (cohortKey, weekNumber, roleId, data) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/role/${encodeURIComponent(roleId)}/attempt/submit`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const gradeCohortRole = (cohortKey, weekNumber, roleId) =>
  request(`/${encodeURIComponent(cohortKey)}/week/${weekNumber}/role/${encodeURIComponent(roleId)}/grade`, {
    method: 'POST',
  });

export const fetchShowTellAssignments = (cohortKey) =>
  request(`/${encodeURIComponent(cohortKey)}/show-tell`);

export const fetchShowTellTopics = (cohortKey) =>
  request(`/${encodeURIComponent(cohortKey)}/show-tell/topics`);

export const assignShowTell = (cohortKey, data) =>
  request(`/${encodeURIComponent(cohortKey)}/show-tell/assign`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const submitShowTellBrief = (cohortKey, assignmentId, brief) =>
  request(`/${encodeURIComponent(cohortKey)}/show-tell/${assignmentId}/brief`, {
    method: 'PUT',
    body: JSON.stringify({ brief }),
  });

export const scoreShowTell = (cohortKey, assignmentId, data) =>
  request(`/${encodeURIComponent(cohortKey)}/show-tell/${assignmentId}/score`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const fetchMockInterviewTemplates = (cohortKey) =>
  request(`/${encodeURIComponent(cohortKey)}/mock-interviews/templates`);

export const fetchMockInterviews = (cohortKey) =>
  request(`/${encodeURIComponent(cohortKey)}/mock-interviews`);

export const assignMockInterviews = (cohortKey, data) =>
  request(`/${encodeURIComponent(cohortKey)}/mock-interviews/assign`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const notifyMockInterview = (cohortKey, cycleId) =>
  request(`/${encodeURIComponent(cohortKey)}/mock-interviews/${cycleId}/notify`, {
    method: 'POST',
  });

export const updateMockInterview = (cohortKey, cycleId, data) =>
  request(`/${encodeURIComponent(cohortKey)}/mock-interviews/${cycleId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const deleteMockInterview = (cohortKey, cycleId) =>
  request(`/${encodeURIComponent(cohortKey)}/mock-interviews/${cycleId}`, {
    method: 'DELETE',
  });

export const scoreMockInterview = (cohortKey, cycleId, data) =>
  request(`/${encodeURIComponent(cohortKey)}/mock-interviews/${cycleId}/score`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const completeMockInterview = (cohortKey, cycleId, data) =>
  request(`/${encodeURIComponent(cohortKey)}/mock-interviews/${cycleId}/complete`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
